package app.smartcoding.realrate.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.ExperimentalMaterial3ExpressiveApi
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.domain.model.AmortizationRow
import app.smartcoding.realrate.domain.model.CreditRating
import app.smartcoding.realrate.presentation.util.formatNumber
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.Padding
import kotlinx.datetime.format.char

private val dateFormat = LocalDate.Format {
    day(Padding.NONE)
    char(' ')
    monthName(MonthNames.ENGLISH_ABBREVIATED)
    char(' '); year()
}

@OptIn(ExperimentalMaterial3ExpressiveApi::class)
@Composable
fun ScheduleTableRow(
    row: AmortizationRow,
    modifier: Modifier = Modifier
) {
    val principalShare = remember(row.principal, row.payment) {
        if (row.payment > 0) (row.principal / row.payment).toFloat().coerceIn(0.01f, 1f) else 0.01f
    }
    val interestShare = remember(row.interest, row.payment) {
        if (row.payment > 0) (row.interest / row.payment).toFloat().coerceIn(0.01f, 1f) else 0.01f
    }

    val colorScheme = MaterialTheme.colorScheme
    val surfaceContainerLow = colorScheme.surfaceContainerLow
    val onSecondaryContainer = colorScheme.onSecondaryContainer
    val secondaryAlpha = colorScheme.secondary.copy(alpha = 0.8f)
    val primary = colorScheme.primary
    val tertiary = colorScheme.tertiary
    val surfaceContainer = colorScheme.surfaceContainer

    val excellentColor = CreditRating.EXCELLENT.color
    val badColor = CreditRating.BAD.color

    val excellentBg = remember(excellentColor) { excellentColor.copy(alpha = 0.1f) }
    val badBg = remember(badColor) { badColor.copy(alpha = 0.1f) }

    val excellentText = remember(excellentColor, surfaceContainer) {
        excellentColor.compositeOver(surfaceContainer)
    }
    val badText = remember(badColor, surfaceContainer) {
        badColor.compositeOver(surfaceContainer)
    }

    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(24.dp)
            .background(surfaceContainerLow),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = row.month.toString(),
            modifier = Modifier.width(22.dp),
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            textAlign = TextAlign.End,
            softWrap = false,
            color = onSecondaryContainer
        )
        Spacer(Modifier.width(8.dp))
        Row(
            modifier = Modifier.width(112.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Text(
                text = remember(row.date) { dateFormat.format(row.date) },
                modifier = Modifier.width(68.dp),
                style = MaterialTheme.typography.labelSmall,
                maxLines = 1,
                softWrap = false,
                color = secondaryAlpha
            )
            Text(
                text = row.payment.formatNumber(),
                modifier = Modifier.width(42.dp),
                style = MaterialTheme.typography.labelSmall,
                maxLines = 1,
                textAlign = TextAlign.End,
                softWrap = false,
                color = primary
            )
        }
        Spacer(Modifier.width(8.dp))
        Box(
            modifier = Modifier.weight(1.5f),
            contentAlignment = Alignment.Center
        ) {
            Row(
                modifier = Modifier.fillMaxSize()
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .weight(principalShare)
                        .background(excellentBg)
                )
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .weight(interestShare)
                        .background(badBg)
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = row.principal.formatNumber(),
                    modifier = Modifier.weight(1f).padding(start = 4.dp),
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    textAlign = TextAlign.Start,
                    softWrap = false,
                    color = excellentText
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    text = row.interest.formatNumber(),
                    modifier = Modifier.weight(1f).padding(end = 4.dp),
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    textAlign = TextAlign.End,
                    softWrap = false,
                    color = badText
                )
            }
        }
        Spacer(Modifier.width(8.dp))
        Text(
            text = row.balance.formatNumber(),
            modifier = Modifier.weight(1f),
            style = MaterialTheme.typography.labelSmall,
            maxLines = 1,
            textAlign = TextAlign.End,
            softWrap = false,
            color = tertiary
        )
        Spacer(Modifier.width(8.dp))
    }
}
